// src/admin/pages/ReturnedOrderListPage.tsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import AdminLayout from '../components/Layout/AdminLayout';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaEdit, FaTrashAlt, FaSearch, FaPlus } from 'react-icons/fa';
import Notification from '../components/Notification/Notification';
import OrderDetailsModal from '../components/OrderDetails/ViewOrderDetails'; // Import the modal component

// Define interfaces for Order and related nested objects
interface Customer {
  id: number;
  name: string;
}

interface ProductItem {
  productId: number;
  productName: string;
  quantity: number;
  pricePerUnit: number;
}

interface Order {
  id: number;
  customer: Customer;
  orderDate: string;
  totalAmount: number;
  orderStatus: 'PENDING' | 'PROCESSING' | 'SHIPPED' | 'DELIVERED' | 'CANCELLED' | 'REFUND';
  paymentStatus: 'PAID' | 'UNPAID' | 'REFUND_PENDING' | 'REFUNDED';
  shippingStatus: 'READY_FOR_DISPATCH' | 'IN_TRANSIT' | 'OUT_FOR_DELIVERY' | 'DELIVERED';
  deliveryAddress: string;
  products: ProductItem[];
}

const ReturnedOrderList: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Notification state
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' | 'info'; } | null>(null);

  // Function to show notification
  const showNotification = (message: string, type: 'success' | 'error' | 'info') => {
    setNotification({ message, type });
  };

  // --- Search, Sort & Pagination States ---
  const [searchTerm, setSearchTerm] = useState<string>('');
  // Filter for REFUND orders is hardcoded
  const [filterPaymentStatus, setFilterPaymentStatus] = useState<string>('all');
  const [filterShippingStatus, setFilterShippingStatus] = useState<string>('all');
  const [sortColumn, setSortColumn] = useState<string>('orderDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc'); // Default to newest orders first
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [ordersPerPage] = useState<number>(10);

  // --- Delete Confirmation Modal States ---
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [orderToDelete, setOrderToDelete] = useState<Order | null>(null);

  // --- Order Details Modal States ---
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  // Dummy Data for demonstration, filtered to include only REFUND orders
  const dummyReturnedOrders: Order[] = useMemo(() => ([
    {
      id: 6, customer: { id: 106, name: 'Diana Prince' }, orderDate: '2025-06-15T10:00:00', totalAmount: 300.00,
      orderStatus: 'REFUND', paymentStatus: 'REFUND_PENDING', shippingStatus: 'DELIVERED',
      deliveryAddress: '303 Willow Rd, Capital City, USA 33445',
      products: [
        { productId: 601, productName: 'Smartwatch', quantity: 1, pricePerUnit: 300.00 }
      ]
    },
    // Add more refund orders if needed for testing
  ]), []);

  // Simulate fetching data
  useEffect(() => {
    setLoading(true);
    // In a real application, you would fetch data from your backend here,
    // explicitly requesting only refund orders, e.g.:
    // fetch('http://localhost:8282/api/orders?status=REFUND')
    //   .then(response => response.json())
    //   .then(data => {
    //     setOrders(data);
    //     setLoading(false);
    //   })
    //   .catch(err => {
    //     showNotification(`Failed to fetch returned orders: ${err.message}`, 'error');
    //     setLoading(false);
    //   });

    // For now, use filtered dummy data after a small delay
    const timer = setTimeout(() => {
      setOrders(dummyReturnedOrders);
      setLoading(false);
    }, 500); // Simulate network delay
    return () => clearTimeout(timer);
  }, [dummyReturnedOrders]);

  // --- Search, Filter & Sort Logic ---
  const filteredAndSortedOrders = useMemo(() => {
    let currentOrders = [...orders];

    // 1. Filter by Search Term
    if (searchTerm) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      currentOrders = currentOrders.filter(order =>
        order.id.toString().includes(lowerCaseSearchTerm) ||
        order.customer.name.toLowerCase().includes(lowerCaseSearchTerm) ||
        order.paymentStatus.toLowerCase().includes(lowerCaseSearchTerm) ||
        order.shippingStatus.toLowerCase().includes(lowerCaseSearchTerm) ||
        order.totalAmount.toFixed(2).includes(lowerCaseSearchTerm) ||
        order.products.some(p => p.productName.toLowerCase().includes(lowerCaseSearchTerm)) // Search by product name
      );
    }

    // This page is specifically for REFUND orders, so ensure it here:
    currentOrders = currentOrders.filter(order => order.orderStatus === 'REFUND');


    // 2. Filter by Payment Status
    if (filterPaymentStatus !== 'all') {
      currentOrders = currentOrders.filter(order => order.paymentStatus === filterPaymentStatus);
    }

    // 3. Filter by Shipping Status
    if (filterShippingStatus !== 'all') {
      currentOrders = currentOrders.filter(order => order.shippingStatus === filterShippingStatus);
    }

    // 4. Sort
    if (sortColumn) {
      currentOrders.sort((a, b) => {
        let valA: any;
        let valB: any;

        if (sortColumn === 'customerName') {
          valA = a.customer.name.toLowerCase();
          valB = b.customer.name.toLowerCase();
        } else if (sortColumn === 'orderDate') {
          valA = new Date(a.orderDate).getTime();
          valB = new Date(b.orderDate).getTime();
        } else if (sortColumn === 'totalAmount') {
          valA = a.totalAmount;
          valB = b.totalAmount;
        } else {
          valA = (a as any)[sortColumn];
          valB = (b as any)[sortColumn];
        }

        if (typeof valA === 'string' && typeof valB === 'string') {
          return sortOrder === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
        } else if (typeof valA === 'number' && typeof valB === 'number') {
          return sortOrder === 'asc' ? valA - valB : valB - valA;
        }
        return 0;
      });
    }

    return currentOrders;
  }, [orders, searchTerm, filterPaymentStatus, filterShippingStatus, sortColumn, sortOrder]);

  // Pagination Logic
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrdersForDisplay = filteredAndSortedOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(filteredAndSortedOrders.length / ordersPerPage);

  const paginate = (pageNumber: number) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  // --- Action Handlers (Simulated) ---
  const handleViewDetails = (order: Order) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
  };

  const handleEditOrder = (id: number) => {
    showNotification(`Editing Returned Order ID: ${id}`, 'info');
    // In a real app: navigate(`/admin/orders/edit/${id}`);
  };

  const initiateDelete = (order: Order) => {
    setOrderToDelete(order);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (orderToDelete) {
      // Simulate API call for deletion
      showNotification(`Returned Order ID: ${orderToDelete.id} deleted successfully! (Simulated)`, 'success');
      setOrders(prev => prev.filter(order => order.id !== orderToDelete.id));
      setShowDeleteModal(false);
      setOrderToDelete(null);
      // In a real app: re-fetch data or update state after successful deletion
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setOrderToDelete(null);
  };

  // Status badge helper function (re-using from OrderListPage)
  const getStatusBadge = (status: string, type: 'order' | 'payment' | 'shipping') => {
    let colorClass = 'bg-gray-100 text-gray-800'; // Default
    switch (type) {
      case 'order':
        switch (status) {
          case 'DELIVERED': colorClass = 'bg-green-100 text-green-800'; break;
          case 'SHIPPED': colorClass = 'bg-blue-100 text-blue-800'; break;
          case 'PROCESSING': colorClass = 'bg-yellow-100 text-yellow-800'; break;
          case 'PENDING': colorClass = 'bg-purple-100 text-purple-800'; break;
          case 'CANCELLED': colorClass = 'bg-red-100 text-red-800'; break;
          case 'REFUND': colorClass = 'bg-indigo-100 text-indigo-800'; break;
        }
        break;
      case 'payment':
        switch (status) {
          case 'PAID': colorClass = 'bg-green-100 text-green-800'; break;
          case 'UNPAID': colorClass = 'bg-red-100 text-red-800'; break;
          case 'REFUND_PENDING': colorClass = 'bg-yellow-100 text-yellow-800'; break;
          case 'REFUNDED': colorClass = 'bg-indigo-100 text-indigo-800'; break;
        }
        break;
      case 'shipping':
        switch (status) {
          case 'DELIVERED': colorClass = 'bg-green-100 text-green-800'; break;
          case 'IN_TRANSIT': colorClass = 'bg-blue-100 text-blue-800'; break;
          case 'OUT_FOR_DELIVERY': colorClass = 'bg-orange-100 text-orange-800'; break;
          case 'READY_FOR_DISPATCH': colorClass = 'bg-purple-100 text-purple-800'; break;
        }
        break;
    }
    return (
      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${colorClass}`}>
        {status.replace(/_/g, ' ')}
      </span>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-48">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container mx-auto p-6 bg-white rounded-lg shadow-md">
        <h2 className="text-3xl font-bold text-gray-800 mb-6">Returned Orders</h2>

        {notification && (
          <Notification
            message={notification.message}
            type={notification.type}
            onClose={() => setNotification(null)}
          />
        )}

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 space-y-4 md:space-y-0 md:space-x-4">
          <div className="w-full md:w-1/3 relative">
            <input
              type="text"
              placeholder="Search orders..."
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1); // Reset to first page on search
              }}
            />
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          </div>

          <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4 w-full md:w-2/3 justify-end">
            {/* Payment Status Filter */}
            <select
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={filterPaymentStatus}
              onChange={(e) => {
                setFilterPaymentStatus(e.target.value);
                setCurrentPage(1); // Reset to first page on filter change
              }}
            >
              <option value="all">All Payment Statuses</option>
              <option value="PAID">Paid</option>
              <option value="UNPAID">Unpaid</option>
              <option value="REFUND_PENDING">Refund Pending</option>
              <option value="REFUNDED">Refunded</option>
            </select>

            {/* Shipping Status Filter */}
            <select
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={filterShippingStatus}
              onChange={(e) => {
                setFilterShippingStatus(e.target.value);
                setCurrentPage(1); // Reset to first page on filter change
              }}
            >
              <option value="all">All Shipping Statuses</option>
              <option value="READY_FOR_DISPATCH">Ready for Dispatch</option>
              <option value="IN_TRANSIT">In Transit</option>
              <option value="OUT_FOR_DELIVERY">Out for Delivery</option>
              <option value="DELIVERED">Delivered</option>
            </select>

            {/* Add New Order Button */}
            <button
              onClick={() => showNotification('Add New Order functionality not implemented.', 'info')}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg shadow-md hover:bg-indigo-700 transition-colors flex items-center justify-center"
            >
              <FaPlus className="mr-2" /> Add New Order
            </button>
          </div>
        </div>


        {/* Orders Table */}
        <div className="overflow-x-auto bg-white rounded-lg shadow overflow-y-auto relative" style={{ maxHeight: '600px' }}>
          <table className="min-w-full leading-normal">
            <thead className="sticky top-0 bg-gray-100">
              <tr>
                <th
                  className="px-5 py-3 border-b-2 border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer"
                  onClick={() => {
                    setSortColumn('id');
                    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                  }}
                >
                  Order ID {sortColumn === 'id' && (sortOrder === 'asc' ? '▲' : '▼')}
                </th>
                <th
                  className="px-5 py-3 border-b-2 border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer"
                  onClick={() => {
                    setSortColumn('customerName'); // Sort by customer.name
                    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                  }}
                >
                  Customer {sortColumn === 'customerName' && (sortOrder === 'asc' ? '▲' : '▼')}
                </th>
                <th
                  className="px-5 py-3 border-b-2 border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer"
                  onClick={() => {
                    setSortColumn('orderDate');
                    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                  }}
                >
                  Order Date {sortColumn === 'orderDate' && (sortOrder === 'asc' ? '▲' : '▼')}
                </th>
                <th
                  className="px-5 py-3 border-b-2 border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer"
                  onClick={() => {
                    setSortColumn('totalAmount');
                    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                  }}
                >
                  Total Amount {sortColumn === 'totalAmount' && (sortOrder === 'asc' ? '▲' : '▼')}
                </th>
                <th className="px-5 py-3 border-b-2 border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Order Status
                </th>
                <th className="px-5 py-3 border-b-2 border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Payment Status
                </th>
                <th className="px-5 py-3 border-b-2 border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Shipping Status
                </th>
                <th className="px-5 py-3 border-b-2 border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {currentOrdersForDisplay.length > 0 ? (
                currentOrdersForDisplay.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                      <p className="text-gray-900 whitespace-no-wrap">#{order.id}</p>
                    </td>
                    <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                      <p className="text-gray-900 whitespace-no-wrap">{order.customer.name}</p>
                    </td>
                    <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                      <p className="text-gray-900 whitespace-no-wrap">
                        {new Date(order.orderDate).toLocaleDateString()}
                      </p>
                    </td>
                    <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                      <p className="text-gray-900 whitespace-no-wrap">${order.totalAmount.toFixed(2)}</p>
                    </td>
                    <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                      {getStatusBadge(order.orderStatus, 'order')}
                    </td>
                    <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                      {getStatusBadge(order.paymentStatus, 'payment')}
                    </td>
                    <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                      {getStatusBadge(order.shippingStatus, 'shipping')}
                    </td>
                    <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => handleViewDetails(order)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Details"
                        >
                          <FaEye className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleEditOrder(order.id)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Edit Order"
                        >
                          <FaEdit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => initiateDelete(order)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete Order"
                        >
                          <FaTrashAlt className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="px-5 py-5 border-b border-gray-200 bg-white text-sm text-center text-gray-600">
                    No returned orders found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex justify-between items-center mt-6">
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-gray-700">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && orderToDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white p-8 rounded-lg shadow-2xl max-w-md w-full border border-gray-300 transform transition-all duration-300 scale-100 ease-out">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Confirm Deletion</h3>
            <p className="text-gray-700 mb-6">Are you sure you want to delete this returned order from <span className="font-semibold text-red-600">"{orderToDelete.customer.name}"</span> with ID <span className="font-semibold text-red-600">#{orderToDelete.id}</span>?</p>
            <p className="text-gray-500 text-sm mb-6">This action cannot be undone.</p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={cancelDelete}
                className="px-6 py-2 rounded-lg border border-gray-400 text-gray-700 hover:bg-gray-100 transition-colors font-semibold shadow-sm"
              >
                Cancel
              </button>
              {/* Kept red for delete */}
              <button
                onClick={confirmDelete}
                className="px-6 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors font-semibold shadow-md"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order Details Modal */}
      {showDetailsModal && selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          onClose={() => setShowDetailsModal(false)}
        />
      )}
    </AdminLayout>
  );
};

export default ReturnedOrderList;